import { ChangeEvent, useContext } from "react";
import CompactCartItem from "./renders/CompactCartItem";
import NormalCartItem from "./renders/NormalCartItem";
import { useUserProduct } from "../../hooks/ProductHooks";
import { addVat } from "../../utils/VatUtils";
import {
  productPriceUnit,
  productStockStatus,
  useProductUrl
} from "../../utils/ProductUtils";
import { useProductTranslations } from "../../hooks/TranslationHooks";
import { useUser } from "../../functional-components/UserContext";
import { CartContext } from "../../functional-components/CartContext";
import useShowPricesWithVat from "../../hooks/useShowPricesWithVat";
import { gtmSendUpdateCart } from "../../utils/GTMUtils";
import { useProductCategories } from "../../hooks/ProductCategoryHooks";

type CartItemArgs = {
  amount: number;
  name: string;
  sku: string;
  realSku?: string;
  image: string;
  isSample: boolean;
};

const CartItem = ({
  item,
  index,
  compact = false
}: {
  item: CartItemArgs;
  index: number;
  compact?: boolean;
}) => {
  const tp = useProductTranslations();
  const { b2b } = useUser();
  const showPricesWithVat = useShowPricesWithVat();
  const { removeFromCart, updateAmount } = useContext(CartContext);
  const productCategories = useProductCategories();

  const { amount, name, sku, realSku, image, isSample } = item;
  const product = useUserProduct(realSku || sku);
  const url = useProductUrl(product || undefined);
  if (!product) {
    return <></>;
  }
  const {
    basePrice: _basePrice,
    unitPrice: _unitBasePrice,
    currency
  } = product.price || {};
  const sampleBasePrice = product?.samplePrice?.basePrice;
  const basePrice = parseFloat(
    (isSample ? sampleBasePrice : _basePrice) || "0"
  );
  const unitBasePrice = isSample ? 0 : parseFloat(_unitBasePrice || "0");
  const unitQuantityFactor = isSample ? undefined : product.unitQuantityFactor;
  const priceUnit = productPriceUnit(unitQuantityFactor);
  const stockStatus = productStockStatus(product, item, tp, b2b);

  const handleRemoveFromCart = () => {
    gtmSendUpdateCart(sku, product, amount, 0, productCategories);
    removeFromCart(index);
  };
  const handleUpdateAmount = (e: ChangeEvent<HTMLInputElement>) => {
    let newAmount = parseInt(e.target.value);
    if (isNaN(newAmount) || newAmount < 1) {
      newAmount = 1;
    }
    gtmSendUpdateCart(sku, product, amount, newAmount, productCategories);
    updateAmount(index, newAmount);
  };

  const CartItemComponent = compact ? CompactCartItem : NormalCartItem;
  return (
    <CartItemComponent
      amount={amount}
      productSku={sku}
      productName={name}
      previewImage={image}
      unitQuantityFactor={unitQuantityFactor ?? undefined}
      currency={currency}
      price={showPricesWithVat ? addVat(basePrice) : basePrice}
      unitPrice={showPricesWithVat ? addVat(unitBasePrice) : unitBasePrice}
      priceUnit={priceUnit}
      stockStatus={stockStatus}
      availableStock={parseInt(product.stock?.availableStock || "999")}
      to={url}
      removeFromCart={handleRemoveFromCart}
      updateAmount={handleUpdateAmount}
      isSample={isSample}
      b2b={b2b}
    />
  );
};

export default CartItem;
